import api from './api';

class APICache {
  constructor() {
    this.cache = new Map();
    this.defaultTTL = 30 * 60 * 1000; // 30 minutes
    this.storagePrefix = 'pilot_api_cache_';
  }

  // Generate cache key
  generateCacheKey(endpoint, params = {}) {
    const paramStr = Object.keys(params).length > 0 ? JSON.stringify(params) : '';
    return `${endpoint}${paramStr}`;
  }

  // Generate storage key for localStorage
  generateStorageKey(cacheKey) {
    return `${this.storagePrefix}${cacheKey}`;
  }

  // Generate expiry storage key
  generateExpiryKey(cacheKey) {
    return `${this.storagePrefix}${cacheKey}_expiry`;
  }

  // Check if cache entry is expired
  isExpired(cacheKey) {
    const expiryTime = localStorage.getItem(this.generateExpiryKey(cacheKey));
    if (!expiryTime) return true;
    
    return Date.now() > parseInt(expiryTime);
  }

  // Save to localStorage
  saveToLocalStorage(cacheKey, data, ttl = this.defaultTTL) {
    try {
      localStorage.setItem(this.generateStorageKey(cacheKey), JSON.stringify(data));
      localStorage.setItem(this.generateExpiryKey(cacheKey), (Date.now() + ttl).toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  // Load from localStorage
  loadFromLocalStorage(cacheKey) {
    try {
      const storageKey = this.generateStorageKey(cacheKey);
      const cachedData = localStorage.getItem(storageKey);
      
      if (cachedData && !this.isExpired(cacheKey)) {
        return JSON.parse(cachedData);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    return null;
  }

  // Get from cache (memory first, then localStorage)
  getFromCache(cacheKey) {
    // Check memory cache first
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (cached.expiry > Date.now()) {
        return cached.data;
      } else {
        this.cache.delete(cacheKey);
      }
    }

    // Check localStorage
    return this.loadFromLocalStorage(cacheKey);
  }

  // Save to cache (both memory and localStorage)
  saveToCache(cacheKey, data, ttl = this.defaultTTL) {
    // Save to memory cache
    this.cache.set(cacheKey, {
      data,
      expiry: Date.now() + ttl
    });

    // Save to localStorage
    this.saveToLocalStorage(cacheKey, data, ttl);
  }

  // Generic method to get data with caching
  async get(endpoint, params = {}, options = {}) {
    const {
      ttl = this.defaultTTL,
      forceRefresh = false,
      transform = null
    } = options;

    const cacheKey = this.generateCacheKey(endpoint, params);

    // Return cached data if available and not force refresh
    if (!forceRefresh) {
      const cachedData = this.getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    try {
      // Build API URL with query parameters
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams ? `${endpoint}?${queryParams}` : endpoint;
      
      const response = await api.get(url);
      
      let data = response.data;
      
      // Apply transformation if provided
      if (transform && typeof transform === 'function') {
        data = transform(data);
      }

      // Cache the response
      this.saveToCache(cacheKey, data, ttl);
      
      return data;
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
      throw error;
    }
  }

  // Clear specific cache entry
  clearCache(endpoint, params = {}) {
    const cacheKey = this.generateCacheKey(endpoint, params);
    
    // Remove from memory cache
    this.cache.delete(cacheKey);
    
    // Remove from localStorage
    localStorage.removeItem(this.generateStorageKey(cacheKey));
    localStorage.removeItem(this.generateExpiryKey(cacheKey));
  }

  // Clear all cache entries
  clearAllCache() {
    // Clear memory cache
    this.cache.clear();
    
    // Clear localStorage entries
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.storagePrefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  // Clear expired cache entries
  clearExpiredCache() {
    // Clear expired memory cache
    for (const [key, value] of this.cache.entries()) {
      if (value.expiry <= Date.now()) {
        this.cache.delete(key);
      }
    }

    // Clear expired localStorage entries
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(this.storagePrefix) && key.endsWith('_expiry')) {
        const expiryTime = parseInt(localStorage.getItem(key));
        if (Date.now() > expiryTime) {
          const cacheKey = key.replace(this.storagePrefix, '').replace('_expiry', '');
          localStorage.removeItem(this.generateStorageKey(cacheKey));
          localStorage.removeItem(key);
        }
      }
    });
  }

  // Get cache stats
  getCacheStats() {
    const memorySize = this.cache.size;
    const localStorageKeys = Object.keys(localStorage).filter(key => 
      key.startsWith(this.storagePrefix) && !key.endsWith('_expiry')
    );
    
    return {
      memoryEntries: memorySize,
      localStorageEntries: localStorageKeys.length,
      totalSize: this.calculateCacheSize()
    };
  }

  // Calculate approximate cache size
  calculateCacheSize() {
    let size = 0;
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(this.storagePrefix)) {
        size += localStorage.getItem(key).length;
      }
    });
    
    return size;
  }
}

// Create singleton instance
const apiCache = new APICache();

// Pre-configured cache methods for common endpoints
export const cacheEndpoints = {
  // Stores cache with 30 minutes TTL
  stores: {
    getAll: (forceRefresh = false) => apiCache.get('/stores', {}, { 
      ttl: 30 * 60 * 1000, 
      forceRefresh,
      transform: (data) => data.stores || []
    }),
    
    getByBranchId: async (branchId, forceRefresh = false) => {
      const stores = await cacheEndpoints.stores.getAll(forceRefresh);
      return stores.find(store => store.branchId === branchId) || null;
    },
    
    getStoreName: async (branchId, forceRefresh = false) => {
      const store = await cacheEndpoints.stores.getByBranchId(branchId, forceRefresh);
      return store ? store.storeName : null;
    },
    
    getRegularStores: async (forceRefresh = false) => {
      const stores = await cacheEndpoints.stores.getAll(forceRefresh);
      return stores.filter(store => !store.isDarkStore);
    },
    
    getDarkStores: async (forceRefresh = false) => {
      const stores = await cacheEndpoints.stores.getAll(forceRefresh);
      return stores.filter(store => store.isDarkStore);
    },
    
    clear: () => apiCache.clearCache('/stores')
  },

  // Users cache with 15 minutes TTL
  users: {
    getAll: (forceRefresh = false) => apiCache.get('/users', {}, { 
      ttl: 15 * 60 * 1000, 
      forceRefresh,
      transform: (data) => data.users || []
    }),
    
    clear: () => apiCache.clearCache('/users')
  },

  // Orders cache with 5 minutes TTL (shorter for more dynamic data)
  orders: {
    getAll: (forceRefresh = false) => apiCache.get('/orders', {}, { 
      ttl: 5 * 60 * 1000, 
      forceRefresh,
      transform: (data) => data.orders || []
    }),
    
    clear: () => apiCache.clearCache('/orders')
  },

  // Branches cache (alias for stores since branches are stores in this system)
  branches: {
    getAll: (forceRefresh = false) => cacheEndpoints.stores.getAll(forceRefresh),
    
    getByBranchId: (branchId, forceRefresh = false) => 
      cacheEndpoints.stores.getByBranchId(branchId, forceRefresh),
    
    getBranchName: (branchId, forceRefresh = false) => 
      cacheEndpoints.stores.getStoreName(branchId, forceRefresh),
    
    clear: () => cacheEndpoints.stores.clear()
  },


};

export default apiCache; 